import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'

import { startMigration } from '@/reducers/costimizer/costimizer.actions'

import { estimateRunMinutes, formatDuration } from '../Costimizer.helpers'

/**
 * Starts a migration test, after saying how long it will take.
 *
 * Never on the click itself: this queues a job that runs for tens of minutes and spends real money on model
 * calls, so the estimate goes in front of it.
 *
 * @param {string[]} subjectKeys the prompts to test; empty tests the whole scorecard
 * @param {number} nSubjects how many prompts the run will cover, for the estimate
 */
export const StartMigrationButton = ({
  scorecardConfigId,
  subjectKeys = [],
  nSubjects,
  perPromptMinutes,
  label = 'Test migration',
  brand = false,
}) => {
  const dispatch = useDispatch()
  const [confirming, setConfirming] = useState(false)

  const minutes = estimateRunMinutes(nSubjects, perPromptMinutes)

  const handleStart = () => {
    dispatch(startMigration(scorecardConfigId, subjectKeys))
    setConfirming(false)
  }

  return (
    <>
      <Button
        small
        basic={!brand}
        brand={brand}
        onClick={() => setConfirming(true)}
        data-testid={
          isEmpty(subjectKeys)
            ? 'costimizer-migrate-button'
            : `costimizer-migrate-subject-${subjectKeys[0]}`
        }
      >
        {label}
      </Button>

      <BasicModal
        title="Start a migration test?"
        show={confirming}
        onClose={() => setConfirming(false)}
        size="tiny"
        data-testid="costimizer-start-confirm"
      >
        <p>
          {`This triggers a Prefect job covering ${nSubjects} prompt${nSubjects === 1 ? '' : 's'}. Expect it to take `}
          <strong>{formatDuration(minutes)}</strong>
          {nSubjects === 1
            ? '.'
            : ', and likely less — a scorecard shares work between its prompts.'}
        </p>
        <p className="muted-text">
          It runs in the background — you can leave this page and come back for the results. Nothing
          goes live until you apply it.
        </p>
        <div className="form-actions is-modal">
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button brand onClick={handleStart} data-testid="costimizer-start-confirm-button">
            Start test
          </Button>
        </div>
      </BasicModal>
    </>
  )
}
